import React, { useEffect, useState } from 'react';
import { useLanguage } from '../assets/js/LanguageContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { clearSession } from '../utils/session';
import '../assets/styles/tokens.css';
import '../assets/styles/Header.css';
import LogoSvg from '../assets/images/header_logo_extracted.png';

const LANGUAGE_OPTIONS = [
  { value: 'es', code: 'ES', label: 'Español' },
  { value: 'en', code: 'EN', label: 'English' },
  { value: 'zh', code: '中文', label: '简体中文' }
];

const NAV_ITEMS = [
  { num: '01', path: '/', key: 'home' },
  { num: '02', path: '/pricing', key: 'pricing' },
  { num: '03', path: '/about-us', key: 'aboutUs' },
  { num: '04', path: '/contact-us', key: 'contactUs' },
  { num: '05', path: '/pin', key: 'home0_join_code' }
];

const LanguageToggle = ({ language, setLanguage, t }) => (
  <div className="tp-lang-toggle">
    {LANGUAGE_OPTIONS.map((option, index) => (
      <React.Fragment key={option.value}>
        {index > 0 && <span className="tp-lang-toggle__sep" aria-hidden="true">|</span>}
        <button
          type="button"
          className={`tp-lang-toggle__btn ${option.value === language ? 'is-active' : ''}`}
          aria-label={t('header_language_select').replace('{language}', option.label)}
          aria-pressed={option.value === language}
          onClick={() => setLanguage(option.value)}
        >
          {option.code}
        </button>
      </React.Fragment>
    ))}
  </div>
);

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = !!localStorage.getItem('token');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userPermissions, setUserPermissions] = useState(null);
  const { language, setLanguage, t } = useLanguage();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (location.hash) {
      scrollToSection(location.hash.substring(1));
    }
  }, [location.hash]);

  useEffect(() => {
    if (location.state && location.state.scrollTo) {
      setTimeout(() => {
        scrollToSection(location.state.scrollTo);
      }, 100);
    }
  }, [location.pathname, location.state]);

  useEffect(() => {
    if (!isAuthenticated) {
      setUserPermissions(null);
      return;
    }

    const fetchUserPermissions = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/usuario', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const userData = await response.json();
        setUserPermissions(userData.has_permissions);
      } catch (error) {
        console.error('Error al obtener permisos del usuario:', error);
      }
    };

    fetchUserPermissions();
  }, [isAuthenticated]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsMenuOpen(false);
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMenuOpen]);

  const handleLogout = () => {
    clearSession();
    window.dispatchEvent(new Event('authChange'));
    navigate('/');
  };

  const closeMenu = () => setIsMenuOpen(false);

  const canAdminister = userPermissions === 4 || userPermissions === 5;

  const navItems = canAdminister
    ? [...NAV_ITEMS, { num: '06', path: '/admin', key: 'admin' }]
    : NAV_ITEMS;

  const isCurrent = (path) => location.pathname === path;

  const handleNavClick = (path) => {
    closeMenu();
    if (isCurrent(path)) window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isHome = location.pathname === '/';

  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    let frame = null;

    const threshold = () => {
      const hero = document.querySelector('.hero');
      return hero ? hero.offsetHeight - hero.offsetTop - 24 : 24;
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > threshold());
        frame = null;
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [location.pathname]);

  return (
    <>
      <header className={`tp-header ${isScrolled ? 'is-scrolled' : ''}`}>
        <div className="tp-header__inner">
          <div className="tp-header__brand">
            <Link to="/" className="tp-header__logo-link" onClick={() => handleNavClick('/')}>
              <img src={LogoSvg} alt="The Pulse" className="tp-header__logo" />
            </Link>
            <span className="tp-header__brand-divider" aria-hidden="true"></span>
          </div>

          <nav className="tp-header__nav" aria-label={t('header_nav_main')}>
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`tp-header__nav-link ${isCurrent(item.path) ? 'is-active' : ''}`}
                aria-current={isCurrent(item.path) ? 'page' : undefined}
                onClick={() => handleNavClick(item.path)}
              >
                {t(item.key)}
              </Link>
            ))}
          </nav>

          <div className="tp-header__actions">
            <LanguageToggle language={language} setLanguage={setLanguage} t={t} />

            {isAuthenticated ? (
              <>
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="tp-header__text-link"
                >
                  {t('dashboard')}
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="tp-btn tp-btn--ink tp-header__cta"
                >
                  {t('logout')}
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="tp-header__text-link">{t('login')}</Link>
                <Link to="/sign-up" className="tp-btn tp-btn--primary tp-header__cta">
                  {t('signup')}
                </Link>
              </>
            )}
  
            <button
              type="button"
              className={`tp-header__burger ${isMenuOpen ? 'is-open' : ''}`}
              aria-label={t(isMenuOpen ? 'header_menu_close' : 'header_menu_open')}
              aria-expanded={isMenuOpen}
              onClick={() => setIsMenuOpen(current => !current)}
            >
              <svg viewBox="0 0 32 32" aria-hidden="true">
                <path
                  className="tp-burger__line tp-burger__line--top-bottom"
                  d="M27 10 13 10C10.8 10 9 8.2 9 6 9 3.5 10.8 2 13 2 15.2 2 17 3.8 17 6L17 26C17 28.2 18.8 30 21 30 23.2 30 25 28.2 25 26 25 23.8 23.2 22 21 22L7 22"
                ></path>
                <path className="tp-burger__line" d="M7 16 27 16"></path>
              </svg>
            </button>
          </div>
        </div>

        <div className={`tp-header__drawer ${isMenuOpen ? 'is-open' : ''}`}>
          <nav className="tp-header__drawer-nav" aria-label={t('header_nav_mobile')}>
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`tp-header__drawer-link ${isCurrent(item.path) ? 'is-active' : ''}`}
                onClick={() => handleNavClick(item.path)}
              >
                {t(item.key)}
              </Link>
            ))}
          </nav>

          <div className="tp-header__drawer-actions">
            <div className="tp-header__drawer-lang">
              <LanguageToggle language={language} setLanguage={setLanguage} t={t} />
            </div>
            {isAuthenticated ? (
              <>
                <button
                  type="button"
                  onClick={() => { closeMenu(); navigate('/dashboard'); }}
                  className="tp-header__text-link"
                >
                  {t('dashboard')}
                </button>
                <button
                  type="button"
                  onClick={() => { closeMenu(); handleLogout(); }}
                  className="tp-btn tp-btn--ink tp-header__cta"
                >
                  {t('logout')}
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="tp-header__text-link" onClick={closeMenu}>{t('login')}</Link>
                <Link to="/sign-up" className="tp-btn tp-btn--primary tp-header__cta" onClick={closeMenu}>
                  {t('signup')}
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {isMenuOpen && <div className="tp-header__overlay" onClick={closeMenu}></div>}

      {!isHome && <div className="tp-header-spacer" aria-hidden="true"></div>}
    </>
  );
};

export default Header;
